import React from 'react'
import { 
  Award, 
  Users, 
  Heart, 
  Shield, 
  Clock, 
  Target, 
  Microscope, 
  ThumbsUp,
  CheckCircle,
  Star
} from 'lucide-react'

const WhyChooseUsSection = () => {
  const advantages = [
    {
      icon: <Award size={32} />,
      title: "15 ans d'expertise",
      description: "Une équipe médicale expérimentée qui maîtrise parfaitement les techniques les plus avancées de greffe capillaire."
    },
    {
      icon: <Microscope size={32} />,
      title: "Techniques de pointe",
      description: "FUE, DHI, et technologies laser dernière génération pour des résultats optimaux et naturels."
    },
    {
      icon: <Shield size={32} />,
      title: "Sécurité maximale",
      description: "Environnement stérile, équipements certifiés et protocoles rigoureux pour votre sécurité."
    },
    {
      icon: <Heart size={32} />,
      title: "Approche personnalisée",
      description: "Chaque patient est unique. Nous adaptons nos traitements à vos besoins spécifiques."
    },
    {
      icon: <Users size={32} />,
      title: "Suivi complet",
      description: "Accompagnement avant, pendant et après l'intervention pour garantir votre satisfaction."
    },
    {
      icon: <Target size={32} />,
      title: "Résultats garantis",
      description: "98% de satisfaction client et résultats naturels visibles dès les premiers mois."
    }
  ]

  const stats = [
    { number: "2000+", label: "Patients traités", icon: <Users size={24} /> },
    { number: "15", label: "Années d'expérience", icon: <Award size={24} /> },
    { number: "98%", label: "Satisfaction client", icon: <ThumbsUp size={24} /> },
    { number: "5★", label: "Note moyenne", icon: <Star size={24} /> }
  ]

  return (
    <section id="pourquoi-nous" className="why-choose-us section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Pourquoi Choisir la Clinique du Grand Paris ?</h2>
          <p className="section-subtitle">
            Découvrez les raisons qui font de la Clinique du Grand Paris 
            le choix privilégié pour vos traitements esthétiques et capillaires.
          </p>
        </div>

        <div className="content-wrapper">
          <div className="advantages-grid">
            {advantages.map((advantage, index) => (
              <div key={index} className="advantage-card">
                <div className="advantage-icon">
                  {advantage.icon}
                </div>
                <h3 className="advantage-title">{advantage.title}</h3>
                <p className="advantage-description">{advantage.description}</p>
              </div>
            ))}
          </div>

          <div className="stats-section">
            <h3 className="stats-title">Nos Chiffres Parlent</h3>
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="wcu-stat-icon">
                    {stat.icon}
                  </div>
                  <div className="wcu-stat-number">{stat.number}</div>
                  <div className="wcu-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="testimonial-highlight">
            <div className="testimonial-content">
              <div className="quote-icon">"</div>
              <blockquote>
                J'ai retrouvé confiance en moi grâce à l'équipe de la Clinique du Grand Paris. 
                Le résultat est parfaitement naturel et a dépassé mes attentes. 
                Je recommande vivement cette clinique !
              </blockquote>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Marc D.</div>
                  <div className="author-detail">Greffe capillaire FUE - Mars 2024</div>
                </div>
                <div className="rating">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="process-section">
            <h3 className="process-title">Notre Processus en 4 Étapes</h3>
            <div className="process-steps">
              <div className="process-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Consultation</h4>
                  <p>Évaluation personnalisée et conseil expert</p>
                </div>
              </div>
              <div className="process-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Planification</h4>
                  <p>Conception du plan de traitement optimal</p>
                </div>
              </div>
              <div className="process-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Intervention</h4>
                  <p>Réalisation avec techniques de pointe</p>
                </div>
              </div>
              <div className="process-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>Suivi</h4>
                  <p>Accompagnement post-traitement</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .why-choose-us {
          background: linear-gradient(135deg, #2C3E50 0%, #34495e 100%);
          color: white;
          position: relative;
          overflow: hidden;
        }

        .why-choose-us::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('/pattern-light.svg') repeat;
          opacity: 0.1;
          z-index: 1;
        }

        .container {
          position: relative;
          z-index: 2;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-title {
          color: white;
        }

        .section-subtitle {
          color: #FFFFFF;
          font-weight: 500;
        }

        .content-wrapper {
          display: flex;
          flex-direction: column;
          gap: 4rem;
        }

        .advantages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .advantage-card {
          background: rgba(255, 255, 255, 0.1);
          -webkit-backdrop-filter: blur(10px);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .advantage-card:hover {
          transform: translateY(-4px);
          background: rgba(255, 255, 255, 0.15);
        }

        .advantage-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--cdgp-gold) 0%, var(--cdgp-primary) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .advantage-title {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: white;
        }

        .advantage-description {
          color: #FFFFFF;
          line-height: 1.6;
          font-weight: 500;
        }

        .stats-section {
          text-align: center;
          background: rgba(255, 255, 255, 0.1);
          -webkit-backdrop-filter: blur(10px);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 3rem;
        }

        .stats-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: white;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 2rem;
        }

        .stat-card {
          text-align: center;
        }

        .wcu-stat-icon {
          color: var(--cdgp-gold);
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
        }

        .wcu-stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--cdgp-gold);
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        .wcu-stat-label {
          color: #FFFFFF;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .testimonial-highlight {
          background: rgba(139, 111, 71, 0.1);
          border: 1px solid rgba(139, 111, 71, 0.3);
          border-radius: 16px;
          padding: 3rem;
          text-align: center;
        }

        .quote-icon {
          font-size: 4rem;
          color: var(--cdgp-gold);
          line-height: 1;
          margin-bottom: 1rem;
        }

        .testimonial-content blockquote {
          font-size: 1.3rem;
          line-height: 1.6;
          color: #FFFFFF;
          margin-bottom: 2rem;
          font-style: italic;
          font-weight: 500;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
        }

        .author-name {
          font-weight: 600;
          color: white;
        }

        .author-detail {
          color: #FFFFFF;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .rating {
          display: flex;
          gap: 0.25rem;
          color: var(--cdgp-gold);
        }

        .process-section {
          text-align: center;
        }

        .process-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: white;
        }

        .process-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          margin-top: 2rem;
        }

        .process-step {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: rgba(255, 255, 255, 0.1);
          -webkit-backdrop-filter: blur(10px);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 1.5rem;
        }

        .step-number {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--cdgp-gold), #6B5B3B);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.3rem;
          color: white;
          flex-shrink: 0;
        }

        .step-content {
          text-align: left;
        }

        .step-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: white;
        }

        .step-content p {
          color: #FFFFFF;
          font-size: 0.9rem;
          line-height: 1.4;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .advantages-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }

          .testimonial-highlight {
            padding: 2rem;
          }

          .testimonial-content blockquote {
            font-size: 1.1rem;
          }

          .testimonial-author {
            flex-direction: column;
            gap: 1rem;
          }

          .process-steps {
            grid-template-columns: 1fr;
          }

          .process-step {
            flex-direction: column;
            text-align: center;
          }

          .step-content {
            text-align: center;
          }
        }

        @media (max-width: 480px) {
          .advantage-card {
            padding: 1.5rem;
          }

          .stats-section {
            padding: 2rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .wcu-stat-number {
            font-size: 2rem;
          }
        }
      `}</style>
    </section>
  )
}

export default WhyChooseUsSection